'use client';
import type { AxiosProgressEvent } from 'axios';
import { apiClient } from '@/lib/local-platform';

export interface UploadFileData {
  id: string;
  filePath: string;
  bucketId: string;
  url: string;
}

// This is the public product limit. Keep it aligned with the server's
// UPLOAD_MAX_SIZE_MB and production nginx client_max_body_size settings.
export const MAX_UPLOAD_SIZE_MB = 300;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;

export type UploadPhase = 'preparing' | 'uploading' | 'storing';

export interface UploadProgress {
  phase: UploadPhase;
  loaded: number;
  total: number;
  percent: number | null;
  bytesPerSecond: number | null;
  elapsedMs: number;
}

export type UploadProgressHandler = (progress: UploadProgress) => void;

export function formatUploadSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Number((bytes / 1024).toFixed(1))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${Number((bytes / 1024 / 1024).toFixed(1))} MB`;
  return `${Number((bytes / 1024 / 1024 / 1024).toFixed(1))} GB`;
}

export function getUploadSizeError(file: { size: number }): string | null {
  if (!Number.isFinite(file.size) || file.size < 0) return '无法读取文件大小，请重新选择文件';
  if (file.size <= MAX_UPLOAD_BYTES) return null;
  return `文件大小 ${formatUploadSize(file.size)}，超过当前上限 ${MAX_UPLOAD_SIZE_MB}MB，请选择不超过 ${MAX_UPLOAD_SIZE_MB}MB 的文件`;
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

// 上传请求超时（毫秒）。避免网络/对象存储卡死时上传按钮无限转圈，
// 超时后 promise 拒绝，调用方的 finally 会收起 loading 并展示可重试错误。
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export async function uploadFile(
  file: File,
  assetType?: string,
  onProgress?: UploadProgressHandler,
): Promise<UploadFileData> {
  const sizeError = getUploadSizeError(file);
  if (sizeError) throw new Error(sizeError);

  const startedAt = now();
  let lastSampleAt = startedAt;
  let lastLoaded = 0;
  let speed = 0;
  onProgress?.({
    phase: 'preparing',
    loaded: 0,
    total: file.size,
    percent: 0,
    bytesPerSecond: null,
    elapsedMs: 0,
  });

  const formData = new FormData();
  if (assetType) formData.append('asset_type', assetType);
  formData.append('file', file);

  const token = sessionStorage.getItem('admin_token');
  const response = await apiClient({
    url: '/api/files/upload',
    method: 'POST',
    data: formData,
    timeout: UPLOAD_TIMEOUT_MS,
    headers: token ? { 'x-admin-token': token } : {},
    onUploadProgress: (event: AxiosProgressEvent) => {
      const timestamp = now();
      const loaded = Math.min(Math.max(Number(event.loaded) || 0, 0), file.size);
      const elapsedMs = Math.max(0, timestamp - startedAt);
      const elapsedSinceSample = (timestamp - lastSampleAt) / 1000;
      if (loaded > lastLoaded && elapsedSinceSample > 0) {
        speed = (loaded - lastLoaded) / elapsedSinceSample;
        lastLoaded = loaded;
        lastSampleAt = timestamp;
      }

      const complete = loaded >= file.size;
      onProgress?.({
        phase: complete ? 'storing' : 'uploading',
        loaded,
        total: file.size,
        percent: complete ? null : Math.min(99, Math.round((loaded / file.size) * 100)),
        bytesPerSecond: speed > 0 ? speed : null,
        elapsedMs,
      });
    },
  });

  return response.data;
}
